import React, { useState } from 'react';
import { CalendarViewProps } from './types/calendar';
import { CalendarContent } from './components/CalendarContent';
import { EventFilterBar } from './components/EventFilterBar';
import { EventTypeFilter } from './components/EventTypeFilter';
import { EventForm } from './EventForm';
import { EventDetailDialog } from './components/EventDetailDialog';
import { EditEventDialog } from './components/EditEventDialog';
import { DeleteEventDialog } from './components/DeleteEventDialog';
import { useCalendarView } from './hooks/useCalendarView';
import { useCalendarEvents } from './hooks/useCalendarEvents';
import { useNaturalLanguageInput } from './hooks/useNaturalLanguageInput';

export const EnhancedCalendarView: React.FC<CalendarViewProps> = ({
  selectedDate,
  onDateSelect
}) => {
  const [showEventForm, setShowEventForm] = useState(false);
  const [showEventDetail, setShowEventDetail] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [eventFormDate, setEventFormDate] = useState<Date>(new Date());
  const [showFilters, setShowFilters] = useState(false);
  const [visibleTypes, setVisibleTypes] = useState<string[]>([
    'interview', 'screening', 'research', 'client', 'prospecting', 'other'
  ]);

  const { calendarRef, viewMode, setViewMode } = useCalendarView(selectedDate);
  
  const {
    events,
    isLoading,
    selectedEvent,
    setSelectedEvent,
    handleEventClick,
    handleEventChange,
    sendReminderForEvent,
    handleEventEdit,
    handleEventDelete
  } = useCalendarEvents(selectedDate, visibleTypes);

  const {
    naturalLanguageInput,
    setNaturalLanguageInput,
    handleNaturalLanguageInput,
    isProcessing
  } = useNaturalLanguageInput();

  const handleDateClick = (arg: { date: Date; allDay: boolean }) => {
    setEventFormDate(arg.date);
    setShowEventForm(true);
  };

  const handleEventClickWrapper = (eventInfo: any) => {
    handleEventClick(eventInfo);
    setShowEventDetail(true);
  };

  const toggleEventType = (typeId: string) => {
    setVisibleTypes(prev =>
      prev.includes(typeId)
        ? prev.filter(id => id !== typeId)
        : [...prev, typeId]
    );
  };

  const handleEventDetailClose = () => {
    setShowEventDetail(false);
    setSelectedEvent(null);
  };

  const handleEditEvent = () => {
    setShowEventDetail(false);
    setShowEditDialog(true);
  };

  const handleDeleteEvent = () => {
    setShowEventDetail(false);
    setShowDeleteDialog(true);
  };

  const handleEditSuccess = () => {
    setShowEditDialog(false);
    setSelectedEvent(null);
    handleEventEdit();
  };

  const handleDeleteSuccess = () => {
    setShowDeleteDialog(false);
    setSelectedEvent(null);
    handleEventDelete();
  };

  return (
    <div className="space-y-4">
      {/* Event Filter Bar */}
      <EventFilterBar
        naturalLanguageInput={naturalLanguageInput}
        setNaturalLanguageInput={setNaturalLanguageInput}
        handleNaturalLanguageInput={handleNaturalLanguageInput}
        isProcessing={isProcessing}
      />

      {/* Event Type Filter */}
      <EventTypeFilter
        showFilters={showFilters}
        setShowFilters={setShowFilters}
        visibleTypes={visibleTypes}
        toggleEventType={toggleEventType}
      />

      {/* Calendar Content */}
      <CalendarContent
        calendarRef={calendarRef}
        viewMode={viewMode}
        formattedEvents={events}
        handleDateClick={handleDateClick}
        handleEventClick={handleEventClickWrapper}
        handleEventChange={handleEventChange}
        setViewMode={setViewMode}
        isLoading={isLoading}
      />

      {/* Event Form Dialog */}
      {showEventForm && (
        <EventForm
          selectedDate={eventFormDate}
          onClose={() => setShowEventForm(false)}
        />
      )}

      {/* Event Detail Dialog */}
      <EventDetailDialog
        event={selectedEvent}
        isOpen={showEventDetail}
        onClose={handleEventDetailClose}
        onEdit={handleEditEvent}
        onDelete={handleDeleteEvent}
      />

      {/* Edit Event Dialog */}
      {selectedEvent && (
        <EditEventDialog
          event={selectedEvent}
          isOpen={showEditDialog}
          onClose={() => setShowEditDialog(false)}
          onSuccess={handleEditSuccess}
        />
      )}

      {/* Delete Event Dialog */}
      {selectedEvent && (
        <DeleteEventDialog
          event={selectedEvent}
          isOpen={showDeleteDialog}
          onClose={() => setShowDeleteDialog(false)}
          onSuccess={handleDeleteSuccess}
        />
      )}
    </div>
  );
};
